import type {
  AndExpression,
  CompareExpression,
  OrExpression,
  RuleExpression,
} from '@tsagko/label-contracts'
import { FilterRow } from './FilterRow'
import {
  appendOperand,
  describeRule,
  emptyCompare,
  emptyGroup,
  isGroup,
  isSimpleCondition,
  removeOperand,
  replaceOperand,
  setTopOp,
  type TopOp,
} from './rule-ops'

interface RuleEditorProps {
  expression: AndExpression | OrExpression
  onChange: (next: AndExpression | OrExpression) => void
}

function NestedGroup({
  group,
  onChange,
  onRemove,
}: {
  group: AndExpression | OrExpression
  onChange: (next: AndExpression | OrExpression) => void
  onRemove: () => void
}) {
  return (
    <div className="rounded border border-stone-300 bg-stone-50 p-2 dark:border-stone-700 dark:bg-stone-950">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-stone-500">Match</span>
          <select
            value={group.op}
            onChange={e => onChange(setTopOp(group, e.target.value as TopOp))}
            className="rounded border border-stone-300 bg-white px-2 py-0.5 text-xs dark:border-stone-700 dark:bg-stone-900"
          >
            <option value="and">all</option>
            <option value="or">any</option>
          </select>
          <span className="text-stone-500">within this group</span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove group"
          className="text-xs text-rose-600 hover:underline"
        >
          Remove group
        </button>
      </div>
      <div className="space-y-1.5">
        {group.operands.map((operand, i) => {
          if (isSimpleCondition(operand)) {
            return (
              <FilterRow
                key={i}
                compare={operand}
                onChange={next => onChange(replaceOperand(group, i, next))}
                onRemove={() => onChange(removeOperand(group, i))}
              />
            )
          }
          return (
            <div key={i} className="rounded border border-dashed border-stone-300 p-2 text-[11px] text-stone-500 dark:border-stone-700">
              Nested groups beyond one level are not supported in Phase 1.
            </div>
          )
        })}
      </div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => onChange(appendOperand(group, emptyCompare()))}
          className="rounded border border-stone-300 px-2 py-1 text-[11px] text-stone-600 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-400 dark:hover:bg-stone-800"
        >
          + Add condition
        </button>
      </div>
    </div>
  )
}

export function RuleEditor({ expression, onChange }: RuleEditorProps) {
  const addCondition = () => {
    onChange(appendOperand(expression, emptyCompare()))
  }
  const addGroup = () => {
    onChange(appendOperand(expression, emptyGroup('or')))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-stone-500">Match</span>
        <select
          value={expression.op}
          onChange={e => onChange(setTopOp(expression, e.target.value as TopOp))}
          className="rounded border border-stone-300 bg-white px-2 py-0.5 text-sm dark:border-stone-700 dark:bg-stone-900"
        >
          <option value="and">all</option>
          <option value="or">any</option>
        </select>
        <span className="text-stone-500">of the following</span>
      </div>
      <div className="space-y-1.5">
        {expression.operands.map((operand, i) => {
          if (isSimpleCondition(operand)) {
            return (
              <FilterRow
                key={i}
                compare={operand}
                onChange={(next: CompareExpression) =>
                  onChange(replaceOperand(expression, i, next))
                }
                onRemove={() => onChange(removeOperand(expression, i))}
              />
            )
          }
          if (isGroup(operand)) {
            return (
              <NestedGroup
                key={i}
                group={operand}
                onChange={next => onChange(replaceOperand(expression, i, next))}
                onRemove={() => onChange(removeOperand(expression, i))}
              />
            )
          }
          return null
        })}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="rc-add-condition"
          onClick={addCondition}
          className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
        >
          + Add condition
        </button>
        <button
          type="button"
          onClick={addGroup}
          className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
        >
          + Add group
        </button>
      </div>
      <div className="rounded border border-sky-200 bg-sky-50 p-3 text-xs italic text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-200">
        Natural language: <strong>{describeRule(expression as RuleExpression) || '(empty)'}</strong>
      </div>
    </div>
  )
}
